import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { TrainingModule, TrainingAssignment } from '../../types/training';

export const ModuleManagement: React.FC = () => {
  const navigate = useNavigate();
  const [modules, setModules] = useState<TrainingModule[]>([]);
  const [assignments, setAssignments] = useState<TrainingAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedModule, setSelectedModule] = useState<TrainingModule | null>(null);
  const [editingModule, setEditingModule] = useState<TrainingModule | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const selectedModuleIdRef = useRef<string | null>(null);

  const loadModules = useCallback(async () => {
    try {
      setLoading(true);
      const [modulesData, assignmentsData] = await Promise.all([
        adminService.getModules(showArchived),
        adminService.getAssignments(),
      ]);
      setModules(modulesData || []);
      setAssignments(assignmentsData || []);
      
      // Update selectedModule if it exists (to refresh modal data)
      if (selectedModuleIdRef.current) {
        const updatedModule = (modulesData || []).find(m => m.id === selectedModuleIdRef.current);
        if (updatedModule) {
          setSelectedModule(updatedModule);
        } else {
          selectedModuleIdRef.current = null;
          setSelectedModule(null);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load modules');
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => {
    loadModules();
  }, [loadModules]);

  const hasNonCompletedAssignments = (moduleId: string): boolean => {
    return assignments.some(
      (assignment) =>
        assignment.moduleId === moduleId &&
        assignment.status !== 'completed'
    );
  };

  const handleArchive = async (id: string, title: string) => {
    const confirmMessage = `Archive "${title}"?\n\nThis will:\n• Delete all training content files from storage\n• Delete exam questions\n• Prevent new assignments\n• Keep the module visible to practitioners who completed it\n• Preserve all certificates\n\nThis action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) return;

    try {
      await adminService.archiveModule(id);
      loadModules();
    } catch (err: any) {
      alert(err.message || 'Failed to archive module');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate('/admin')}
        className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
      >
        <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Dashboard
      </button>
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Training Modules</h2>
          <p className="mt-1 text-sm text-gray-600">Create and manage training content</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Module
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Filter Toggle */}
      <div className="flex items-center justify-between mb-4">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Show archived modules</span>
        </label>
      </div>

      {/* Modules Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {modules.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white rounded-lg shadow">
            <p className="text-gray-500">No training modules yet. Create your first module to get started.</p>
          </div>
        ) : (
          modules.map((module) => (
            <div key={module.id} className="bg-white shadow rounded-lg overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 truncate">{module.title}</h3>
                  <div className="flex items-center space-x-2">
                    {module.examId && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Has Exam
                      </span>
                    )}
                    {module.archived && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Archived {module.archivedAt && `• ${new Date(module.archivedAt).toLocaleDateString()}`}
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{module.description}</p>
                
                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-2">Content Files: {module.contentFiles?.length || 0}</p>
                  {module.contentFiles && module.contentFiles.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {module.contentFiles.map((file) => (
                        <span
                          key={file.id}
                          className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700"
                        >
                          {file.fileType}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      selectedModuleIdRef.current = module.id;
                      setSelectedModule(module);
                    }}
                    disabled={module.archived}
                    className={`text-sm ${
                      module.archived
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-blue-600 hover:text-blue-900'
                    }`}
                  >
                    Manage
                  </button>
                  <div className="flex items-center gap-4">
                    <button 
                      onClick={() => setEditingModule(module)}
                      disabled={module.archived}
                      className={`text-sm ${
                        module.archived
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => navigate(`/admin/exams/${module.id}`)}
                      disabled={module.archived}
                      className={`text-sm ${
                        module.archived
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-purple-600 hover:text-purple-900'
                      }`}
                    >
                      Exam
                    </button>
                    <div className="relative group inline-flex">
                      <button
                        onClick={() => handleArchive(module.id, module.title)}
                        disabled={hasNonCompletedAssignments(module.id) || module.archived}
                        className={`text-sm ${
                          hasNonCompletedAssignments(module.id) || module.archived
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-orange-600 hover:text-orange-900'
                        }`}
                      >
                        Archive
                      </button>
                      {(hasNonCompletedAssignments(module.id) || module.archived) && (
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                          {module.archived 
                            ? 'Module is already archived'
                            : 'Training module already assigned to members'}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Module Modal */}
      {showAddModal && (
        <AddModuleModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            loadModules();
          }}
        />
      )}

      {/* Module Details Modal */}
      {selectedModule && (
        <ModuleDetailsModal
          module={selectedModule}
          onClose={() => {
            selectedModuleIdRef.current = null;
            setSelectedModule(null);
          }}
          onUpdate={loadModules}
        />
      )}

      {/* Edit Module Modal */}
      {editingModule && (
        <EditModuleModal
          module={editingModule}
          onClose={() => setEditingModule(null)}
          onSuccess={() => {
            setEditingModule(null);
            loadModules();
          }}
        />
      )}
    </div>
  );
};

interface AddModuleModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const AddModuleModal: React.FC<AddModuleModalProps> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      setLoading(true);
      await adminService.createModule(formData);
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to create module');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Create Training Module</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
              Module Title
            </label>
            <input
              type="text"
              id="title"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="description"
              required
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Module'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface ModuleDetailsModalProps {
  module: TrainingModule;
  onClose: () => void;
  onUpdate: () => void;
}

const ModuleDetailsModal: React.FC<ModuleDetailsModalProps> = ({ module: initialModule, onClose, onUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [module, setModule] = useState(initialModule);

  // Sync with prop changes
  useEffect(() => {
    setModule(initialModule);
  }, [initialModule]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const uploadedFile = await adminService.uploadContent(module.id, file, setUploadProgress);
      
      // Immediately update local state with the new file
      setModule(prev => ({
        ...prev,
        contentFiles: [...(prev.contentFiles || []), uploadedFile]
      }));
      
      // Also refresh from backend
      onUpdate();
    } catch (err: any) {
      alert(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await adminService.deleteContent(module.id, fileId);
      
      // Immediately update local state by removing the file
      setModule(prev => ({
        ...prev,
        contentFiles: (prev.contentFiles || []).filter(f => f.id !== fileId)
      }));
      
      // Also refresh parent list
      onUpdate();
    } catch (err: any) {
      alert(err.message || 'Failed to delete file');
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">{module.title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-6">{module.description}</p>

        {/* Upload Section */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Upload Content</label>
          <input
            type="file"
            onChange={handleFileUpload}
            disabled={uploading}
            accept=".ppt,.pptx,.doc,.docx,.pdf,.mp4,.mov,.txt"
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          {uploading && (
            <div className="mt-2">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Uploading... {uploadProgress}%</p>
            </div>
          )}
        </div>

        {/* Content Files */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Content Files</h4>
          {!module.contentFiles || module.contentFiles.length === 0 ? (
            <p className="text-sm text-gray-500">No files uploaded yet</p>
          ) : (
            <div className="space-y-2">
              {module.contentFiles.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {file.fileType}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{file.fileName}</p>
                      <p className="text-xs text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteFile(file.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};


interface EditModuleModalProps {
  module: TrainingModule;
  onClose: () => void;
  onSuccess: () => void;
}

const EditModuleModal: React.FC<EditModuleModalProps> = ({ module, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: module.title,
    description: module.description,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      setLoading(true);
      await adminService.updateModule(module.id, formData);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to update module');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Edit Training Module</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="edit-title" className="block text-sm font-medium text-gray-700">
              Module Title
            </label>
            <input
              type="text"
              id="edit-title"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="edit-description"
              required
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
